// src/components/financials/DonationTypeConfiguration.js
import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, TextField, Button, Chip, Grid, Divider, MenuItem 
} from '@mui/material';
import { AddCircle, Label } from '@mui/icons-material';
import { db } from '../../services/firebase';
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';

const DonationTypeConfiguration = () => {
  const { userProfile } = useAuth();
  const [types, setTypes] = useState([]);
  const [funds, setFunds] = useState([]);
  const [newType, setNewType] = useState('');
  const [selectedFundId, setSelectedFundId] = useState('');

  const churchId = userProfile?.churchId;

  useEffect(() => {
    if (churchId) { fetchTypes(); fetchFunds(); }
  }, [churchId]);

  const fetchTypes = async () => {
    const snap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'donationTypes'));
    setTypes(snap.docs.map(d => ({ id: d.id, ...d.data() })).sort((a,b) => a.name.localeCompare(b.name)));
  };

  const fetchFunds = async () => {
    const snap = await getDocs(collection(db, 'churches', churchId, 'financials', 'data', 'funds'));
    setFunds(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  };

  const handleAdd = async () => {
    if (!newType.trim()) return;
    await addDoc(collection(db, 'churches', churchId, 'financials', 'data', 'donationTypes'), {
        name: newType.trim(),
        defaultFundId: selectedFundId || null,
        createdAt: serverTimestamp()
    });
    setNewType(''); setSelectedFundId(''); fetchTypes();
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this type?")) return;
    await deleteDoc(doc(db, 'churches', churchId, 'financials', 'data', 'donationTypes', id));
    fetchTypes();
  };

  return (
    <Paper sx={{ p: 3, height: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Label color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">Giving Types & Default Funds</Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
                <TextField 
                    fullWidth size="small" label="Giving Reason (e.g. Sunday School)"
                    value={newType} onChange={(e) => setNewType(e.target.value)} 
                />
            </Grid>
            <Grid item xs={12}>
                <TextField 
                    select fullWidth size="small" label="Default Accounting Fund"
                    value={selectedFundId} onChange={(e) => setSelectedFundId(e.target.value)}
                    helperText="Which fund should this money go to automatically?"
                >
                    <MenuItem value=""><em>-- Manual Selection Required --</em></MenuItem>
                    {funds.map(f => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
                </TextField>
            </Grid>
            <Grid item xs={12}>
                <Button fullWidth variant="contained" startIcon={<AddCircle />} onClick={handleAdd}>Save Type</Button>
            </Grid>
        </Grid>

        <Divider sx={{ mb: 2 }} />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {types.map(t => (
                <Chip 
                    key={t.id} 
                    label={`${t.name} → ${funds.find(f=>f.id===t.defaultFundId)?.name || 'Manual Fund'}`} 
                    onDelete={() => handleDelete(t.id)} 
                    color="primary" variant="outlined" 
                />
            ))}
        </Box>
    </Paper>
  );
};

export default DonationTypeConfiguration;